import type { JSX } from 'preact';
import type { NthWeekday, RecurrenceFreq, RecurrenceRule, Weekday } from '../domain/types';
import { weekdayShort } from '../domain/format';

const FREQ_LABELS: Record<RecurrenceFreq, string> = {
  once: 'Just once',
  daily: 'Every day',
  weekly: 'Every week',
  biweekly: 'Every 2 weeks',
  monthly: 'Every month',
};

/** Ordered by how often an instructor picks them, not alphabetically. */
const FREQ_ORDER: RecurrenceFreq[] = ['weekly', 'biweekly', 'monthly', 'daily', 'once'];
const WEEKDAYS: Weekday[] = [1, 2, 3, 4, 5, 6, 7];
const NTH_OPTIONS: { value: NthWeekday; label: string }[] = [
  { value: 1, label: 'First' },
  { value: 2, label: 'Second' },
  { value: 3, label: 'Third' },
  { value: 4, label: 'Fourth' },
  { value: -1, label: 'Last' },
];

/**
 * Switching frequency has to produce a *complete* rule for the new shape, not a
 * partially-filled one, so each branch supplies its own defaults seeded from
 * whatever the previous rule knew.
 */
export function changeFrequency(
  current: RecurrenceRule,
  freq: RecurrenceFreq,
  defaultWeekday: Weekday = 2,
): RecurrenceRule {
  const knownWeekdays =
    current.freq === 'weekly' || current.freq === 'biweekly' ? current.byWeekday : [];
  const fallbackWeekdays: readonly Weekday[] =
    knownWeekdays.length > 0 ? knownWeekdays : [defaultWeekday];

  switch (freq) {
    case 'once':
      return { freq: 'once' };
    case 'daily':
      return { freq: 'daily' };
    case 'weekly':
      return { freq: 'weekly', byWeekday: fallbackWeekdays };
    case 'biweekly':
      return { freq: 'biweekly', byWeekday: fallbackWeekdays };
    case 'monthly':
      return current.freq === 'monthly'
        ? current
        : { freq: 'monthly', mode: 'byMonthDay', monthDay: 1 };
    default: {
      const exhaustive: never = freq;
      throw new Error(`Unknown frequency: ${String(exhaustive)}`);
    }
  }
}

export function toggleWeekday(rule: RecurrenceRule, day: Weekday): RecurrenceRule {
  if (rule.freq !== 'weekly' && rule.freq !== 'biweekly') {
    return rule;
  }
  const has = rule.byWeekday.includes(day);
  const byWeekday = has
    ? rule.byWeekday.filter((existing) => existing !== day)
    : [...rule.byWeekday, day].sort((a, b) => a - b);
  return { ...rule, byWeekday };
}

/**
 * Frequency is a plain select rather than a row of chips: five frequency chips
 * plus seven weekday chips was most of the old form's visual noise, and only the
 * weekday choice benefits from being tappable at a glance.
 */
export function RecurrenceEditor(props: {
  value: RecurrenceRule;
  error?: string | undefined;
  defaultWeekday?: Weekday;
  onChange: (rule: RecurrenceRule) => void;
}): JSX.Element {
  const { value, onChange } = props;

  return (
    <div class="stack">
      <label class="field-control">
        <span class="field-label">Repeats</span>
        <select
          value={value.freq}
          onChange={(event) =>
            onChange(
              changeFrequency(
                value,
                (event.target as HTMLSelectElement).value as RecurrenceFreq,
                props.defaultWeekday,
              ),
            )
          }
        >
          {FREQ_ORDER.map((freq) => (
            <option key={freq} value={freq}>
              {FREQ_LABELS[freq]}
            </option>
          ))}
        </select>
      </label>

      {value.freq === 'weekly' || value.freq === 'biweekly' ? (
        <div class="day-toggles" role="group" aria-label="Days of the week">
          {WEEKDAYS.map((day) => (
            <button
              key={day}
              type="button"
              class={`day-toggle${value.byWeekday.includes(day) ? ' selected' : ''}`}
              aria-pressed={value.byWeekday.includes(day)}
              aria-label={weekdayShort(day)}
              onClick={() => onChange(toggleWeekday(value, day))}
            >
              {weekdayShort(day).slice(0, 2)}
            </button>
          ))}
        </div>
      ) : null}

      {value.freq === 'monthly' ? (
        <div class="inline-row">
          <label class="field-control">
            <span class="field-label">Pattern</span>
            <select
              value={value.mode}
              onChange={(event) =>
                onChange(
                  (event.target as HTMLSelectElement).value === 'byMonthDay'
                    ? { freq: 'monthly', mode: 'byMonthDay', monthDay: 1 }
                    : { freq: 'monthly', mode: 'byNthWeekday', weekday: 2, nth: 1 },
                )
              }
            >
              <option value="byMonthDay">On a date</option>
              <option value="byNthWeekday">On a weekday</option>
            </select>
          </label>

          {value.mode === 'byMonthDay' ? (
            <label class="field-control">
              <span class="field-label">Day of month</span>
              <input
                type="number"
                min={1}
                max={31}
                value={value.monthDay}
                onInput={(event) =>
                  onChange({
                    freq: 'monthly',
                    mode: 'byMonthDay',
                    monthDay: Number((event.target as HTMLInputElement).value),
                  })
                }
              />
            </label>
          ) : (
            <>
              <label class="field-control">
                <span class="field-label">Which</span>
                <select
                  value={String(value.nth)}
                  onChange={(event) =>
                    onChange({
                      ...value,
                      nth: Number((event.target as HTMLSelectElement).value) as NthWeekday,
                    })
                  }
                >
                  {NTH_OPTIONS.map((option) => (
                    <option key={option.value} value={String(option.value)}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label class="field-control">
                <span class="field-label">Weekday</span>
                <select
                  value={String(value.weekday)}
                  onChange={(event) =>
                    onChange({
                      ...value,
                      weekday: Number((event.target as HTMLSelectElement).value) as Weekday,
                    })
                  }
                >
                  {WEEKDAYS.map((day) => (
                    <option key={day} value={String(day)}>
                      {weekdayShort(day)}
                    </option>
                  ))}
                </select>
              </label>
            </>
          )}
        </div>
      ) : null}

      {props.error !== undefined ? (
        <span class="field-error" role="alert">
          {props.error}
        </span>
      ) : null}
    </div>
  );
}
